import { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import { RatingStars } from '@/components/ui/RatingStars';
import { toast } from '@/components/ui/Toast';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '@/lib/firebase';
import { compressImage } from '@/lib/image';
import { callSubmitReview } from '@/lib/callables';
import {
  REVIEW_TEXT_MAX_CHARS,
  categoryHasWearLife,
  type ServiceCategory,
} from '@makhe/shared';

interface Props {
  open: boolean;
  onClose: () => void;
  bookingId: string;
  category?: ServiceCategory;
}

const MAX_REVIEW_MEDIA = 8;
const safeName = (n: string) => n.replace(/[^a-zA-Z0-9._-]/g, '_');

export function ReviewModal({ open, onClose, bookingId, category }: Props) {
  const [rating, setRating] = useState(0);
  const [text, setText] = useState('');
  // Photos and/or video — multiple allowed.
  const [media, setMedia] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [feltSafe, setFeltSafe] = useState<boolean | null>(null);

  const willFollowUp = category ? categoryHasWearLife(category) : false;

  const addMedia = (files: FileList) => {
    const picked = Array.from(files).filter(
      (f) => f.type.startsWith('image/') || f.type.startsWith('video/'),
    );
    if (picked.length === 0) return;
    setMedia((m) => [...m, ...picked].slice(0, MAX_REVIEW_MEDIA));
  };

  const submit = async () => {
    if (rating < 1) return toast.error('Pick a rating.');
    setBusy(true);
    try {
      const photoURLs: string[] = [];
      let videoReviewURL: string | undefined;
      for (const file of media) {
        if (file.size > 100 * 1024 * 1024) {
          throw new Error(`"${file.name}" is over 100 MB — compress it first.`);
        }
        const path = `reviews/${bookingId}/${Date.now()}-${safeName(file.name)}`;
        if (file.type.startsWith('video/')) {
          const r = ref(storage, path);
          await uploadBytes(r, file, { contentType: file.type });
          videoReviewURL = await getDownloadURL(r); // one video per review
        } else {
          const blob = await compressImage(file);
          const r = ref(storage, path);
          await uploadBytes(r, blob, { contentType: blob.type || 'image/jpeg' });
          photoURLs.push(await getDownloadURL(r));
        }
      }
      await callSubmitReview({
        bookingId,
        rating,
        textReview: text || undefined,
        videoReviewURL,
        photoURLs: photoURLs.length ? photoURLs : undefined,
        feltSafe: feltSafe ?? undefined,
      });
      toast.success('Review posted. Thanks!');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Leave a review">
      <div className="space-y-4">
        <div>
          <p className="text-sm text-makhe-ink/70">Rate your experience</p>
          <RatingStars value={rating} onChange={setRating} size="lg" />
        </div>
        <Textarea
          label="What was it like?"
          rows={3}
          value={text}
          maxLength={REVIEW_TEXT_MAX_CHARS}
          onChange={(e) => setText(e.target.value)}
          hint={`${text.length} / ${REVIEW_TEXT_MAX_CHARS}`}
        />
        <div>
          <label className="block text-sm text-makhe-ink/80">
            Photos or a video (optional)
            {media.length < MAX_REVIEW_MEDIA && (
              <input
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={(e) => e.target.files?.length && addMedia(e.target.files)}
                className="mt-2 block w-full text-sm text-makhe-ink/70 file:mr-3 file:rounded-full file:border-0 file:bg-makhe-line/50 file:px-4 file:py-2 file:text-makhe-ink"
              />
            )}
          </label>
          {media.length > 0 && (
            <div className="mt-3 grid grid-cols-4 gap-2">
              {media.map((file, i) => {
                const isVideo = file.type.startsWith('video/');
                const url = URL.createObjectURL(file);
                return (
                  <div key={`${file.name}-${i}`} className="group relative aspect-square overflow-hidden rounded-lg bg-makhe-line/40">
                    {isVideo ? (
                      <video src={url} muted playsInline className="h-full w-full object-cover" />
                    ) : (
                      <img src={url} alt="" className="h-full w-full object-cover" />
                    )}
                    <button
                      type="button"
                      aria-label="Remove"
                      onClick={() => setMedia((m) => m.filter((_, j) => j !== i))}
                      className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-black/60 text-xs text-white"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div>
          <p className="text-sm text-makhe-ink/70">Did you feel safe? (private — not shown publicly)</p>
          <p className="mt-1 text-xs text-makhe-muted">
            &quot;No&quot; alerts our safety team privately. It does not appear on your public review.
          </p>
          <div className="mt-2 flex gap-2">
            <Button
              type="button"
              size="sm"
              variant={feltSafe === true ? 'primary' : 'secondary'}
              onClick={() => setFeltSafe(true)}
            >
              Yes
            </Button>
            <Button
              type="button"
              size="sm"
              variant={feltSafe === false ? 'primary' : 'secondary'}
              onClick={() => setFeltSafe(false)}
            >
              No
            </Button>
          </div>
        </div>
        {willFollowUp && (
          <p className="rounded-lg bg-makhe-bg/60 p-3 text-xs text-makhe-muted">
            We’ll check back in about 3 weeks to see how long it lasts — that’s the bit
            that helps the next student decide.
          </p>
        )}
        <Button onClick={submit} loading={busy} className="w-full">
          Post review
        </Button>
      </div>
    </Modal>
  );
}
